import React, {useMemo} from 'react';

import {SchemaCollaborator} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import * as Context from 'Context';
import {
  ActionMethod,
  ActionOrigin,
  LegacyAssaySchemaType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {DefaultSchemaCollaboratorsConfigurationSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {filterFieldsByDraftTemplateFields} from 'results/analysis-integration/AnalysisTemplateIntegrationUtils';
import {SCHEMA_TYPE_NAME} from 'results/assay-schemas/AssaySchemaConstants';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import SchemaWithOrgPermissionsCreateFlow from 'schemas/SchemaWithOrgPermissionsCreateFlow';
import {capitalize} from 'util/NameUtil';

import {AssaySchemaCreateFlowProps} from './AssaySchemaEditorTypes';
import {getSpecForRequest, AssayRunSchemaSpecOverrides} from './AssaySchemaEditorUtils';
import AssaySchemaMetadataSettings from './AssaySchemaMetadataSettings';

export default function AssaySchemaCreateFlow(props: AssaySchemaCreateFlowProps): JSX.Element {
  const {runFields, templateFields} = useMemo(() => {
    if (props.schemaType !== LegacyAssaySchemaType.ASSAY_RUN || !props.fields) {
      return {runFields: props.fields ?? [], templateFields: []};
    }

    return filterFieldsByDraftTemplateFields(props.fields, props.draftTemplateFields);
  }, [props.schemaType, props.fields, props.draftTemplateFields]);

  const getDraftOverrides = (): AssayRunSchemaSpecOverrides | null => {
    if (props.schemaType !== LegacyAssaySchemaType.ASSAY_RUN) {
      return null; // Don't send overrides for result schemas
    }
    if (!props.effectiveAnalysisTemplateId) {
      return null; // No template selected
    }
    return {
      analysisTemplateId: props.effectiveAnalysisTemplateId,
      runFields,
      templateFields,
      analysisTemplateVariableMapping: props.draftAnalysisTemplateVariableMapping,
    };
  };

  const createAssaySchema = (
    collaborators?: Array<SchemaCollaborator>,
    overrides?: AssayRunSchemaSpecOverrides | null
  ): void => {
    const spec = getSpecForRequest(props, overrides);
    if (spec) {
      Context.using(
        {
          analyticsMetadata: {
            actionOrigin: ActionOrigin.SETTINGS_ASSAY_SCHEMA_PAGE_UI,
            actionMethod: ActionMethod.CREATE_BUTTON,
          },
        },
        null,
        () => {
          AssaySchemaActions.createAssaySchema({
            ownerId: props.ownerId,
            name: props.name,
            schemaType: props.schemaType,
            spec,
            sessionId: props.sessionId,
            sqlIdentifier:
              BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME && props.sqlIdentifier
                ? props.sqlIdentifier
                : null,
            isSpecJsonFormat: false,
            publishDefaultDataTableConfig: props.publishDefaultDataTableConfig,
            collaborators,
          });
        }
      );
    }
  };

  const defaultSchemaCollaboratorsSchemaType =
    props.schemaType === LegacyAssaySchemaType.ASSAY_RESULT
      ? DefaultSchemaCollaboratorsConfigurationSchemaType.RESULT
      : DefaultSchemaCollaboratorsConfigurationSchemaType.RUN;

  function renderMetadataSettings(handleNext: () => void): JSX.Element {
    return (
      <AssaySchemaMetadataSettings
        {...props}
        schema={null}
        handleNext={handleNext}
        onCreateAssaySchema={createAssaySchema}
      />
    );
  }

  return (
    <SchemaWithOrgPermissionsCreateFlow
      createSchema={(collaborators) => {
        createAssaySchema(collaborators, getDraftOverrides());
      }}
      defaultSchemaCollaboratorsSchemaType={defaultSchemaCollaboratorsSchemaType}
      metadataTitle={`${capitalize(SCHEMA_TYPE_NAME[props.schemaType])} Settings`}
      isSchemaPermissionsEnabled={BenchlingConfig.PERMISSIONS_ENABLE_ASSAY_SCHEMA_PERMISSIONS}
      ownerId={props.ownerId}
      organizations={props.organizations}
      renderMetadataSettings={renderMetadataSettings}
    />
  );
}
